import React, { useEffect, useMemo, useState } from "react";
import { FileCheck } from "lucide-react";
import { ExpandableHeader } from "../../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import Button from "../../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import ClaimForm from "../../../Claims/TabContent/ClaimForm";
import notify from "../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { claimsApi } from "../../../Claims/api/claimsApi";
import {
  detailsToFormValues,
  formatStopLabel,
  isOpenClaim,
  mapSummaryDto,
} from "../../../Claims/claimsMappers";
import { getTodayLocalDate } from "../../../Claims/claimFormUtils";
import {
  getApiErrorMessage,
  loadClaimForEdit,
  saveClaim,
} from "../../../Claims/claimsService";
import {
  ClaimStatusBadge,
  ClaimTypeBadge,
  formatClaimMoney,
  formatClaimDate,
} from "../../../Claims/claimsShared";

const getShipmentOrigin = (shipmentDetails) => {
  const stops = [...(shipmentDetails?.shipmentStops || [])].sort(
    (a, b) => (a.stopSequence ?? a.stopNumber ?? 0) - (b.stopSequence ?? b.stopNumber ?? 0)
  );
  return stops[0]?.stopName || shipmentDetails?.portOfOrigin || "—";
};

const getShipmentDestination = (shipmentDetails) => {
  const stops = [...(shipmentDetails?.shipmentStops || [])].sort(
    (a, b) => (a.stopSequence ?? a.stopNumber ?? 0) - (b.stopSequence ?? b.stopNumber ?? 0)
  );
  const last = stops[stops.length - 1];
  return last?.stopName || shipmentDetails?.portOfDestination || "—";
};

const newClaimFormValues = (shipmentContext, stopOptions) => ({
  shipmentNumber: shipmentContext.shipmentNumber,
  shipmentOfficeCode: shipmentContext.officeCode || "",
  poNumber: shipmentContext.poNumber,
  origin: shipmentContext.origin,
  destination: shipmentContext.destination,
  carrierName: shipmentContext.carrierName,
  relatedStopId: stopOptions[stopOptions.length - 1]?.id || "",
  relatedStop: stopOptions[stopOptions.length - 1]?.label || "",
  dateClaimFiled: getTodayLocalDate(),
});

const ShipmentClaims = ({
  shipmentDetails,
  setSectionRef,
  canFileClaim = false,
  // LGX-1044: filing and editing are both Shipper Admin capabilities. Kept as its own prop
  // so a view-only role (carrier) can open a claim without the form turning writable.
  canEditClaim = canFileClaim,
  isCarrierView = false,
  openFormRequest = false,
  onOpenFormHandled,
  onClaimsChange,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [claims, setClaims] = useState([]);
  const [isLoadingClaims, setIsLoadingClaims] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFieldErrors, setSaveFieldErrors] = useState(null);
  const [isLoadingClaim, setIsLoadingClaim] = useState(false);

  const shipmentNumber = shipmentDetails?.shipmentNumber;
  const officeCode = shipmentDetails?.officeCode || "";

  const loadClaims = async () => {
    if (!shipmentNumber || !officeCode) {
      setClaims([]);
      onClaimsChange?.(0);
      return;
    }

    try {
      setIsLoadingClaims(true);
      const response = await claimsApi.getClaimsByShipment(shipmentNumber, officeCode);
      const mapped = (response || []).map((dto) => mapSummaryDto(dto, officeCode));
      setClaims(mapped);
      onClaimsChange?.(mapped.length);
    } catch (error) {
      setClaims([]);
      onClaimsChange?.(0);
      notify.error({
        title: "Claims Unavailable",
        message: getApiErrorMessage(error, "Failed to load shipment claims."),
      });
    } finally {
      setIsLoadingClaims(false);
    }
  };

  useEffect(() => {
    loadClaims();
  }, [shipmentNumber, officeCode]);

  useEffect(() => {
    if (openFormRequest && canFileClaim) {
      setExpanded(true);
      setSelectedClaim(null);
      setShowForm(true);
      onOpenFormHandled?.();
    }
  }, [openFormRequest, canFileClaim, onOpenFormHandled]);

  const openCount = useMemo(
    () => claims.filter((claim) => isOpenClaim(claim)).length,
    [claims]
  );

  const stopOptions = useMemo(() => {
    const stops = [...(shipmentDetails?.shipmentStops || [])].sort(
      (a, b) => (a.stopSequence ?? a.stopNumber ?? 0) - (b.stopSequence ?? b.stopNumber ?? 0)
    );
    return stops.map((stop, index) => ({
      id: stop.id,
      value: String(stop.id),
      label: formatStopLabel(stop, index),
    }));
  }, [shipmentDetails?.shipmentStops]);

  const shipmentContext = useMemo(
    () => ({
      shipmentNumber: shipmentDetails?.shipmentNumber || "",
      poNumber: shipmentDetails?.poNumber || "",
      origin: getShipmentOrigin(shipmentDetails),
      destination: getShipmentDestination(shipmentDetails),
      carrierName: shipmentDetails?.carrierName || "—",
      stops: stopOptions,
      officeCode,
    }),
    [shipmentDetails, stopOptions, officeCode]
  );

  const openNewClaimForm = () => {
    setSelectedClaim(null);
    setShowForm(true);
  };

  const openClaimDetail = async (claim) => {
    try {
      setIsLoadingClaim(true);
      setShowForm(true);
      const loaded = await loadClaimForEdit(
        claim.claimNumber,
        officeCode,
        claim.officeCode
      );
      setSelectedClaim(loaded);
    } catch (error) {
      notify.error({
        title: "Unable to Load Claim",
        message: getApiErrorMessage(error, "Failed to load claim details."),
      });
    } finally {
      setIsLoadingClaim(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedClaim(null);
  };

  const handleSubmitClaim = async (form, { submit, draft } = {}) => {
    if (selectedClaim ? !canEditClaim : !canFileClaim) return;

    if (draft && !form.relatedStopId) {
      notify.warn({
        title: "Draft Not Saved",
        message: "Related stop is required to save a draft.",
      });
      return;
    }

    try {
      setIsSaving(true);
      setSaveFieldErrors(null);
      await saveClaim({
        form,
        officeCode,
        existingClaim: selectedClaim,
        mode: "shipment",
        submit,
        draft,
      });
      await loadClaims();
      closeForm();
      notify.success({
        title: draft ? "Draft Saved" : submit ? "Claim Submitted" : "Claim Saved",
        message: draft
          ? "Claim draft saved."
          : submit
            ? `Claim filed on ${shipmentNumber}.`
            : "Claim updated successfully.",
      });
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to save claim.");
      // A 409 on save is the duplicate Claim Number guard — put it on the field the user
      // has to fix, not only in a toast that disappears.
      if (error?.response?.status === 409) {
        setSaveFieldErrors({ claimNumber: message });
      }
      notify.error({
        title: error?.response?.status === 409 ? "Duplicate Claim Number" : "Save Failed",
        message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formInitialValues = selectedClaim
    ? detailsToFormValues(selectedClaim)
    : newClaimFormValues(shipmentContext, stopOptions);

  const formKey = selectedClaim?.claimNumber || "new-claim";
  const showClaimForm = showForm && (canFileClaim || canEditClaim || isCarrierView);

  return (
    <>
      <h3 ref={setSectionRef?.("Claims")} className="text-fluid-lg font-semibold mb-fluid-2">
        Claims
      </h3>

      <ExpandableHeader
        headerSection="Claims"
        icon={FileCheck}
        headerType="primary"
        isExpanded={expanded}
        onToggle={() => setExpanded((prev) => !prev)}
        rightSection={
          openCount > 0 ? (
            <span className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200 rounded px-fluid-1.5 py-fluid-1 text-fluid-xs font-medium uppercase tracking-wide">
              {openCount} OPEN
            </span>
          ) : null
        }
      >
        <div className="bg-[#F9FAFE] p-fluid-3 space-y-fluid-3 dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text font-roboto">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext font-semibold">
              {isLoadingClaims
                ? "Loading claims..."
                : claims.length === 0
                  ? "No claims filed on this shipment"
                  : `${claims.length} claim${claims.length === 1 ? "" : "s"} filed on this shipment`}
            </span>
            <div className="flex-1" />
            {canFileClaim && (
              <Button
                text={showForm && !selectedClaim ? "Close Form" : "File a Claim"}
                variant={showForm && !selectedClaim ? "outline" : "primary"}
                onClick={() => (showForm && !selectedClaim ? closeForm() : openNewClaimForm())}
              />
            )}
          </div>

          {claims.length > 0 && (
            <div className="overflow-x-auto bg-white dark:bg-acs-dark-surface rounded-md border border-gray-200 dark:border-acs-dark-border">
              <table className="w-full border-collapse text-fluid-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-acs-dark-border bg-[#FAFAFB] dark:bg-acs-dark-hover">
                    {[
                      "Claim Number",
                      "Claim Type",
                      "Status",
                      "Related Stop",
                      "Date Filed",
                      "Total Claim Amount",
                      "Amount Approved",
                      "Amount Paid",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left px-3 py-2.5 text-gray-500 dark:text-acs-dark-subtext font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim) => {
                    const isSelected =
                      selectedClaim?.claimNumber === claim.claimNumber && showForm;
                    return (
                      <tr
                        key={claim.claimNumber}
                        onClick={() => openClaimDetail(claim)}
                        className={`border-b border-gray-200 dark:border-acs-dark-border cursor-pointer transition-none ${
                          isSelected
                            ? "bg-custom-bg-light-purple dark:bg-acs-dark-hover"
                            : "hover:bg-gray-50 dark:hover:bg-acs-dark-hover"
                        }`}
                      >
                        <td className="px-3 py-3 font-semibold text-[#223688] dark:text-[#6DE1C2] whitespace-nowrap">
                          {claim.claimNumber}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <ClaimTypeBadge type={claim.claimType} />
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <ClaimStatusBadge status={claim.status} />
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-acs-light-text dark:text-acs-dark-text">
                          {claim.relatedStop || "—"}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-acs-light-text dark:text-acs-dark-text">
                          {formatClaimDate(claim.dateFiled)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {formatClaimMoney(claim.totalClaimAmount)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {formatClaimMoney(claim.amountApproved)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {formatClaimMoney(claim.amountPaid)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {showClaimForm && (
            <div className="border-t border-gray-200 dark:border-acs-dark-border pt-fluid-2">
              {selectedClaim && (
                <div className="flex items-center justify-between mb-fluid-2">
                  <h4 className="text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text m-0">
                    {canEditClaim ? "Edit Claim" : "Claim Details"} — {selectedClaim.claimNumber}
                  </h4>
                </div>
              )}
              {isLoadingClaim ? (
                <div className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext font-semibold">
                  Loading claim...
                </div>
              ) : (
                <ClaimForm
                  key={formKey}
                  mode="shipment"
                  compact
                  officeCode={officeCode}
                  readOnly={selectedClaim ? !canEditClaim : !canFileClaim}
                  isSaving={isSaving}
                  showStatusSection={Boolean(selectedClaim)}
                  /*
                    LGX-1044: a submitted claim used to render every field frozen, so the
                    Claims section on Shipment Detail offered no way to edit a filed claim.
                    Read-only is now driven only by whether the user may edit.
                  */
                  detailsLocked={false}
                  shipmentContext={shipmentContext}
                  initialValues={formInitialValues}
                  onCancel={closeForm}
                  onSubmit={handleSubmitClaim}
                  fieldErrors={saveFieldErrors}
                />
              )}
            </div>
          )}
        </div>
      </ExpandableHeader>
    </>
  );
};

export default ShipmentClaims;
